import { CommonModule, DecimalPipe, NgClass } from '@angular/common';
import { Component, Input, OnInit, HostListener, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AppService, Page } from '@core/services/app.service';
import { GlobalHandlerService } from '@core/services/global-handler.service';
import { SharedService } from '@core/services/shared.service';
import { TranslateModule } from '@ngx-translate/core';
import { Cart, CartItems } from '@shared/classes/my-orders';
import { CheckoutTimerComponent } from '@shared/features/checkout-timer/checkout-timer.component';
import { ConfirmModalComponent } from '@shared/features/modals/confirm-modal/confirm-modal.component';
import { SplitDecimalPipe } from '@shared/pipes/decimal-font.pipe';
import { LocalizedDatePipe } from '@shared/pipes/localized-date.pipe';
import { BsModalService } from 'ngx-bootstrap/modal';
import { CurvedHeaderComponent } from '@shared/components/curved-header/curved-header.component';
import { PreventParentScrollDirective } from '@shared/directives/prevent-scrolling.directive';
import { Subscription } from 'rxjs';
import { groupCartItemsByGroupId, isDefined, setLastAndFirstElemetOfGroups } from '@shared/heplers/helpers';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { GoogleAnalyticsService } from '@core/services/google-analytics.service';
import { environment } from 'src/environments/environment';
import { CartItemDetailsComponent } from '@shared/components/cart-item-details/cart-item-details.component';

@Component({
  selector: 'app-cart-summary',
  templateUrl: './cart-summary.component.html',
  styleUrl: './cart-summary.component.scss',
  animations: [
    trigger('slideUpAnimation', [
      state('up', style({
        transform: 'translateY(0)'
      })),
      state('down', style({
        transform: 'translateY(100%)'
      })),
      transition('down => up', [
        animate('0.4s ease-out')
      ]),
      transition('up => down', [
        animate('0.35s ease-in')
      ])
    ]),
    trigger('slideUpAnimationFast', [
      state('up', style({
        transform: 'translateY(0)'
      })),
      state('down', style({
        transform: 'translateY(100%)'
      })),
      transition('down => up', [
        animate('0.2s ease-out')
      ]),
      transition('up => down', [
        animate('0.15s ease-in')
      ])
    ])
  ],
  imports: [
    DecimalPipe,
    NgClass,
    SplitDecimalPipe,
    TranslateModule,
    CheckoutTimerComponent,
    RouterModule,
    LocalizedDatePipe,
    CurvedHeaderComponent,
    PreventParentScrollDirective,
    CommonModule,
    CartItemDetailsComponent
  ]
})
export class CartSummaryComponent implements OnInit, OnDestroy {
  @Input() isHeaderBreakpoint: boolean;
  @Input() isSmallScreen: boolean;
  @Input() isCustom: boolean;
  @Input() customDataPage: boolean;

  cart: Cart = {} as Cart;
  cartItems: CartItems[] = [];
  refreshCartListener: Subscription;
  showCart: boolean = false;
  showLoadingCart: boolean = false;
  slideBottomSheetModal: 'up' | 'down' = 'down';
  isClosingDropdownModal: boolean = false;
  page = new Page();
  isMiddleScreeen = window.innerWidth < 820

  addToCartTrack: {
    multiple: boolean;
    itemIds: number[];
    content_name: string;
    value: number;
  };
  
  fadingOutItems: Set<number> = new Set();

  constructor(
    private appService: AppService,
    private modalService: BsModalService,
    private globalHandlerService: GlobalHandlerService,
    private sharedService: SharedService,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {
    this.page.size = 1000;
  }

  ngOnInit(): void {
    this.getCart(false, false);
    this.refreshCartListener = this.sharedService.refreshCartObserver.subscribe((res: any) => {
      if (res) {
        if (res.addToCart) {
          this.addToCartTrack = {
            multiple: res.isMultiple,
            itemIds: res.cartItemIds,
            content_name: res.content_name,
            value: res.value
          };
          this.getCart(false, true);
        } else if (res.cart) {
          this.cart = res.cart;
        }
      } else {
        this.getCart(false, false);
      }
      this.cartItems = [];
    });
  }

  ngOnDestroy(): void {
    this.refreshCartListener.unsubscribe();
  }

  onToggleShowCart() {   
    if (this.showCart === true) {
      this.closeCartModal();
    } else {
      this.showCartModal();
    }
  }

  showCartModal() {
    this.showLoadingCart = true;
    this.getCart(true, false);
    this.slideBottomSheetModal = "up";
    this.showCart = true;
  }

  closeCartModal() {
    this.slideBottomSheetModal = "down";
    this.isClosingDropdownModal = true;
    setTimeout(() => {
      this.showCart = false;
      this.isClosingDropdownModal = false;
    }, 300);
  }

  getCart(getCartItems: boolean, trackAddToCart: boolean) {
    this.appService.getElement(`selling/web/carts/myCart`).subscribe((response: Cart) => {
      if (response) {
        this.cart = response;
        if (getCartItems && !trackAddToCart) {
          this.getCartItems(false);
        } else if (trackAddToCart) {
          if (this.addToCartTrack.multiple) {
            this.getCartItems(true)
          } else {
            this.trackPixelAddToCart();
          }
        } else {
          this.showLoadingCart = false;
        }
      } else {
        this.cart = {} as Cart;
        this.cart.totalElements = 0;
        this.cartItems = [];
        this.showLoadingCart = false;
      }
    }, (error) => {
      this.cart = { totalElements: 0 } as Cart;
      this.showLoadingCart = false;
      this.globalHandlerService.showError(error);
    });
  }

  getCartItems(trackMultipleAddToCart: boolean) {
    this.appService.getElement(`selling/web/carts/myCart/cartItems`, this.page).subscribe((response: any) => {
      if (response && response.content && response.content.length > 0) {
        this.cartItems = response.content;
        this.cartItems = setLastAndFirstElemetOfGroups(groupCartItemsByGroupId(this.cartItems), 'purchaseGroupId');
        if (trackMultipleAddToCart && this.addToCartTrack) {
          this.googleAnalyticsService.setMultipleAddToCartOrDeleteCartDataLayer(this.addToCartTrack.itemIds, this.cartItems, 'add_to_cart');
          this.trackPixelAddToCart();
        }
      } else {
        this.cartItems = [];
      }
      this.showLoadingCart = false;
    }, (error) => {
      this.cartItems = [];
      this.showLoadingCart = false;
      this.globalHandlerService.showError(error);
    });
  }

  trackPixelAddToCart() {
    if (!environment.allowPixelTrack) { return; }
    const pixels = this.sharedService.getPixelIds();
    if (pixels.length <= 0) { return; }
    this.sharedService.grantPixelTracking();
    if (this.addToCartTrack.multiple) {
      this.cartItems.forEach(item => {
        if(this.addToCartTrack.itemIds.length > 0 && this.addToCartTrack.itemIds.indexOf(item.resourceId) >= 0){
          const fullDescription = [
            item.description,
            item.description2,
            item.description3,
            item.description4
          ].filter(description => isDefined(description)).join(',');
          this.trackPixelAddToCartItem(pixels, fullDescription, item.amount);
        }
      });
    } else {
      this.trackPixelAddToCartItem(pixels, this.addToCartTrack.content_name, this.addToCartTrack.value);
    }
    this.addToCartTrack = null;
  }

  trackPixelAddToCartItem(pixels: number[], content_name: string, value: number) {
    pixels.forEach(pixel => {
      (window as any).fbq('trackSingle', pixel.toString(), 'AddToCart', {
        content_name: content_name,
        currency: 'EUR',
        value: value,
        event_id : this.cart.code
      });
    })
  }

  onDeleteCartItems(isCart: boolean, cartItem: CartItems = null) {
    const bsModalRef = this.modalService.show(ConfirmModalComponent, { class: 'modal-sm modal-dialog-centered' });
    this.sharedService.scrollParentWindow();
    
    bsModalRef.content.onConfirm = () => { 
      if (isCart) {
        this.deleteCart();
      } else {
        this.deleteCartItem(cartItem);
      }
    };
  }

  deleteCart() {
    this.appService.deleteElement(`selling/web/carts/myCart`).subscribe(() => {
      this.closeCartModal();
      this.cart = { totalElements: 0 } as Cart;
      this.cartItems = [];
      this.sharedService.refreshSeatViewerObserver.emit({ action: 'DELETE', seatId: null });
      this.sharedService.cartDeleteEventObserver.emit();
    }, (error) => {
      this.globalHandlerService.showError(error);
    });
  }

  deleteCartItem(cartItem: CartItems) {
    this.appService.deleteElement(`selling/web/carts/myCart/cartItems/${cartItem.resourceId}`).subscribe(() => {
      this.startDeleteAnimation(cartItem.resourceId);
      if (cartItem.seatId || cartItem.entranceId) {
        this.sharedService.refreshSeatViewerObserver.emit({
          action: 'DELETE',
          seatId: cartItem.seatId || cartItem.entranceId
        });
      }
      this.sharedService.cartDeleteEventObserver.emit();
    }, (error) => {
      this.globalHandlerService.showError(error);
    });
  }

  private startDeleteAnimation(itemId: number) {
    this.fadingOutItems.add(itemId);
    setTimeout(() => {
      this.cartItems = this.cartItems.filter(item => item.resourceId !== itemId);
      this.fadingOutItems.delete(itemId);
      if (this.cartItems.length > 0) {
        this.getCart(true, false);
      } else {
        this.closeCartModal();
        this.cart = { totalElements: 0 } as Cart;
      }
    }, 400);
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const clickInsideModal = target.closest('.cart-container');
    const clickOnConfirmationModal = target.closest('.modal-content');
    if (!this.isHeaderBreakpoint && this.showCart && !clickOnConfirmationModal && !clickInsideModal) {
        this.closeCartModal();
    }
  }
}